import { Component, ElementRef, Injector, OnInit } from '@angular/core';
import { ResetCancellablePaymentsList } from '@app/finxone-web-frontend/app/lib/actions/payment.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountdownService } from '@app/finxone-web-frontend/app/lib/services/countdown-service/countdown-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { PaymentState } from '@app/finxone-web-frontend/app/lib/state/payment.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { handleRedirection } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/cta-redirection.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  ActionButtonOutcomesType,
  baseCssConfigStyle,
  BaseCssProperties,
  ProviderClass,
  TextAdditionalConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import Handlebars from 'handlebars';
import { catchError, fromEvent, merge, Subject, takeUntil, tap } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-cancellable-payments-info-widget',
  templateUrl: './cancellable-payments-info-widget.component.html',
})
export class CancellablePaymentsInfoWidgetComponent implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public contentStyle: baseCssConfigStyle;
  public colorTextAlignStyle: baseCssConfigStyle;
  private attri: TextAdditionalConfig;
  private content = '';
  public renderedContent = '';
  public selectedTextStyleClass = '';
  public isWorkflow: boolean;
  public paymentCancellationSettings:
    | {
        isPaymentCancellationEnabled: boolean;
        paymentCancellationTimer: number;
      }
    | undefined;

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<any>,
    private countDownService: CountdownService,
    private store: Store,
    private elRef: ElementRef,
    private injector: Injector,
    private accountService: AccountService,
    private alertHandlerService: AlertHandlerService,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
  ) {}

  ngOnInit(): void {
    this.onLoadWidgetConfiguration();
    this.isWorkflow = this.configService.getActiveConfigType()?.isWorkflow;
    this.paymentCancellationSettings = this.store.selectSnapshot(
      ProjectSettingsState.getProjectSettings,
    )?.projectConfig?.paymentCancellationSettings;
    if (!this.paymentCancellationSettings || !this.paymentCancellationSettings.isPaymentCancellationEnabled) {
      this.toggleWidgetVisibility(true);
    }
    if (this.paymentCancellationSettings?.isPaymentCancellationEnabled) {
      this.setupTimer();
      this.setupAppResumeDetection();
      this.countDownService.countdown$.pipe(takeUntil(this.destroy$)).subscribe((countdown) => {
        if (countdown === '00') {
          this.toggleWidgetVisibility(true);
        }
      });
    }
  }

  private updateTemplate(): void {
    if (!this.content) return;
    const template = Handlebars.compile(this.content);
    buildHandleBarModel(this.store, this.content, this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  private toggleWidgetVisibility(shouldHide: boolean): void {
    const hostEl = this.elRef.nativeElement as HTMLElement;
    if (!hostEl) return;

    const parentEl = hostEl.parentElement;
    if (!parentEl) return;
    if (!this.isWorkflow) {
      shouldHide ? parentEl.classList.add('d-none') : parentEl.classList.remove('d-none');
      this.destroy$.complete(); // Complete the destroy$ subject to clean up app resume event subscriptions
      this.store.dispatch(new ResetCancellablePaymentsList());
    }
  }

  private setupTimer() {
    const expiryReversalAt = this.store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData
      ?.expiryReversalAt;

    if (this.paymentCancellationSettings?.isPaymentCancellationEnabled && expiryReversalAt) {
      this.startTimerIfReversalTimeNotExpired(expiryReversalAt);
    } else {
      this.toggleWidgetVisibility(true);
    }
  }

  private startTimerIfReversalTimeNotExpired(expiryReversalAt: string): void {
    const scheduledTime = dayjs(expiryReversalAt);
    const currentTime = dayjs();
    const timeDifference = scheduledTime.diff(currentTime, 'second');

    if (timeDifference > 0) {
      this.countDownService.startCountdown(timeDifference, false);
    } else {
      this.toggleWidgetVisibility(true);
    }
  }

  private onLoadWidgetConfiguration(): void {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
    }

    this.content = this.attri?.widgetProperties?.textContent;
    this.selectedTextStyleClass = this.attri?.widgetProperties?.textStyleClass ?? '';

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.V_ALIGN,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BORDER,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.BOX_SHADOW,
    ]);

    this.colorTextAlignStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
    ]);

    this.updateTemplate();
  }

  public onActionClick(): void {
    const cancellablePaymentsList = this.store.selectSnapshot(PaymentState.getCancellablePaymentsList);
    this.accountService
      .removeScheduledPayment(cancellablePaymentsList[0].id)
      .pipe(
        tap(() => {
          this.toggleWidgetVisibility(true);
          handleRedirection(this.attri.widgetProperties, this.injector, ActionButtonOutcomesType.SUCCESS);
        }),
        catchError((error) => {
          console.error('Payment cancellation failed:', error);
          handleRedirection(this.attri.widgetProperties, this.injector, ActionButtonOutcomesType.FAILURE);
          throw error;
        }),
      )
      .subscribe();
  }

  private setupAppResumeDetection(): void {
    // Web: Page Visibility API for tab switching
    const visibilityChange$ = fromEvent(document, 'visibilitychange').pipe(
      tap(() => {
        if (document.visibilityState === 'visible') {
          this.onAppResume();
        }
      }),
    );

    // Mobile: Custom events for app resume
    const resumeEvent$ = fromEvent(document, 'resume').pipe(tap(() => this.onAppResume()));

    // Also listen for focus events as fallback
    const focusEvent$ = fromEvent(window, 'focus').pipe(tap(() => this.onAppResume()));

    // Merge all observables in one subscription and take until destroy in order to avoid multiple subscriptions
    merge(visibilityChange$, resumeEvent$, focusEvent$).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private onAppResume(): void {
    // Refresh the timer when app resumes
    this.setupTimer();
  }
}
